import { useRef, useEffect, useState } from "react";
import {
  Users,
  Wallet,
  Coins,
  DollarSign,
  TrendingUp,
  UserCheck,
  Lock,
  Flame,
  Trophy,
  Bot,
  Twitter,
  Zap,
  ArrowUpRight,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Percent,
  Calendar,
} from "lucide-react";

const STATS_ENDPOINT =
  process.env.REACT_APP_STATS_ENDPOINT || "/project/api/stats";

// Custom hook for intersection observer (iOS-safe alternative to useInView)
const useIsVisible = (ref, options = {}) => {
  const [isVisible, setIsVisible] = useState(false);
  
  useEffect(() => {
    if (!ref.current) return;
    
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setIsVisible(true);
        if (options.once !== false) {
          observer.disconnect();
        }
      }
    }, { threshold: options.amount || 0.3 });
    
    observer.observe(ref.current);
    return () => observer.disconnect();
  }, [ref, options.once, options.amount]);
  
  return isVisible;
};

// Loading skeleton component
const StatSkeleton = ({ delay = 0 }) => (
  <div
    className="glass rounded-2xl p-5 text-center animate-fade-in-up"
    style={{ 
      animationDelay: `${delay}s`,
      WebkitBackfaceVisibility: 'hidden',
    }}
  >
    <div className="flex justify-center mb-2">
      <div className="w-5 h-5 rounded bg-white/10 animate-pulse" />
    </div>
    <div className="h-3 w-20 bg-white/10 rounded mx-auto mb-3 animate-pulse" />
    <div className="h-8 w-24 bg-white/10 rounded mx-auto animate-pulse" />
  </div>
);

const CountUpCard = ({ label, value, delay, icon: Icon, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useIsVisible(ref, { once: true, amount: 0.3 });
  const [displayValue, setDisplayValue] = useState("0");
  const [hasAnimated, setHasAnimated] = useState(false);
  const previousValueRef = useRef(0);

  // Extract numeric value and detect decimals
  const numericValue = parseFloat(value.replace(/[^\d.-]/g, "")) || 0;
  const hasDecimals = value.includes(".") && !/\.00/.test(value);
  const decimalPlaces = hasDecimals
    ? (value.split(".")[1]?.replace(/[^\d]/g, "").length || 2)
    : 0;

  useEffect(() => {
    if (!inView) return;
    
    const startValue = previousValueRef.current;
    const duration = 1500;
    const startTime = performance.now();
    let frameId;

    const animateValue = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);
      
      // Ease out cubic
      const eased = 1 - Math.pow(1 - progress, 3);
      const current = startValue + (numericValue - startValue) * eased;

      if (decimalPlaces > 0) {
        setDisplayValue(current.toLocaleString(undefined, {
          minimumFractionDigits: Math.min(decimalPlaces, 2),
          maximumFractionDigits: Math.min(decimalPlaces, 6),
        }));
      } else {
        setDisplayValue(Math.floor(current).toLocaleString());
      }

      if (progress < 1) {
        frameId = requestAnimationFrame(animateValue);
      } else {
        previousValueRef.current = numericValue;
        setHasAnimated(true);
      }
    };

    frameId = requestAnimationFrame(animateValue);
    
    return () => {
      if (frameId) cancelAnimationFrame(frameId);
    };
  }, [inView, numericValue, decimalPlaces]);

  return (
    <div
      ref={ref}
      className={`glass rounded-2xl p-5 text-center hover:scale-[1.03] transition-all duration-300 transform-gpu ${
        hasAnimated || inView ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-5'
      }`}
      style={{ 
        transitionDelay: `${delay}s`,
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransform: hasAnimated || inView ? 'translate3d(0,0,0)' : 'translate3d(0,20px,0)',
      }}
    >
      {Icon && (
        <div className="flex justify-center mb-2">
          <Icon className="w-5 h-5 text-purple-400" />
        </div>
      )}
      <h4 className="text-xs text-gray-400 mb-1">{label}</h4>
      <p className="text-xl sm:text-2xl font-bold text-purple-300 truncate">
        {prefix}{displayValue}{suffix}
      </p>
    </div>
  );
};

const FadeInCard = ({ label, value, delay, icon: Icon, prefix = "", suffix = "" }) => {
  const ref = useRef(null);
  const inView = useIsVisible(ref, { once: true, amount: 0.3 });

  return (
    <div
      ref={ref}
      className={`glass rounded-2xl p-5 text-center hover:scale-[1.03] transition-all duration-400 transform-gpu ${
        inView ? 'opacity-100 scale-100' : 'opacity-0 scale-95'
      }`}
      style={{ 
        transitionDelay: `${delay}s`,
        WebkitBackfaceVisibility: 'hidden',
        WebkitTransform: inView ? 'translate3d(0,0,0) scale(1)' : 'translate3d(0,0,0) scale(0.95)',
      }}
    >
      {Icon && (
        <div className="flex justify-center mb-2">
          <Icon className="w-5 h-5 text-purple-400" />
        </div>
      )}
      <h4 className="text-xs text-gray-400 mb-1">{label}</h4>
      <p className="text-xl sm:text-2xl font-bold text-purple-300 truncate">
        {prefix}{parseFloat(value).toLocaleString(undefined, {
          minimumFractionDigits: value.includes(".") ? 2 : 0,
          maximumFractionDigits: value.includes(".") ? 6 : 0,
        })}{suffix}
      </p>
    </div>
  );
};

const INITIAL_STATS = {
  totalUsers: 0,
  totalClosedAccounts: 0,
  solRefunded: 0,
  price: 0,
  marketCap: 0,
  holders: 0,
  totalStakedPercent: 0,
  percentBurned: 0,
};

const INITIAL_EXTENDED_STATS = {
  highestBurnAllTime: 0,
  highestBurn24h: 0,
  highestBurn7d: 0,
  buybacksSol: 0,
  xAccountsConnected: 0,
  botUsersTotal: 0,
  raidPayoutsSol: 0,
  contestPayoutsSol: 0,
  solSavedForUsers: 0,
  totalStakes: 0,
  totalStakedRys: 0,
  walletsConnected: 0,
  stakingRewardsSol: 0,
};

const INITIAL_STAKING_APY = {
  days: [],
  averageApy: 0,
  totalStakingPool: 0,
  validDays: 0,
};

const EXTENDED_STATS_ENDPOINT = "/project/api/extended-stats";
const STAKING_APY_ENDPOINT = "/project/api/staking-apy";

// Section title component - defined outside to prevent re-creation
const SectionTitle = ({ children }) => (
  <h4 className="text-lg font-semibold text-gray-300 mb-6 flex items-center justify-center gap-2">
    {children}
  </h4>
);

export default function Stats() {
  const [apiStats, setApiStats] = useState(INITIAL_STATS);
  const [extendedStats, setExtendedStats] = useState(INITIAL_EXTENDED_STATS);
  const [stakingApy, setStakingApy] = useState(INITIAL_STAKING_APY);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showExtended, setShowExtended] = useState(false);

  // Fetch main stats
  useEffect(() => {
    let isMounted = true;

    const fetchStats = async () => {
      try {
        const response = await fetch(STATS_ENDPOINT);

        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const data = await response.json();

        if (!data?.success || !data?.data) {
          throw new Error("API returned an error");
        }

        const payload = data.data;

        if (isMounted) {
          setApiStats({
            totalUsers: Number(payload.totalUsers) || 0,
            totalClosedAccounts: Number(payload.totalClosedAccounts) || 0,
            solRefunded: Number(payload.solRefunded) || 0,
            price: Number(payload.price) || 0,
            marketCap: Number(payload.marketCap) || 0,
            holders: Number(payload.holders) || 0,
            totalStakedPercent: Number(payload.totalStakedPercent) || 0,
            percentBurned: Number(payload.percentBurned) || 0,
          });
          setError(null);
          setIsLoading(false);
        }
      } catch (err) {
        if (isMounted) {
          console.error("Failed to load public stats:", err);
          setError("Unable to load live stats right now.");
          setIsLoading(false);
        }
      }
    };

    fetchStats();
    const interval = setInterval(fetchStats, 10000); // Poll every 10 seconds

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Fetch extended stats
  useEffect(() => {
    let isMounted = true;

    const fetchExtended = async () => {
      try {
        const response = await fetch(EXTENDED_STATS_ENDPOINT);
        if (!response.ok) return;
        
        const data = await response.json();
        if (!data?.success || !data?.data) return;
        
        if (isMounted) {
          setExtendedStats(data.data);
        }
      } catch (err) {
        console.error("Failed to load extended stats:", err);
      }
    };

    fetchExtended();
    const interval = setInterval(fetchExtended, 10000); // Poll every 10 seconds (backend serves cached)

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  // Fetch staking APY data
  useEffect(() => {
    let isMounted = true;

    const fetchApyData = async () => {
      try {
        const response = await fetch(STAKING_APY_ENDPOINT);
        if (!response.ok) return;
        
        const data = await response.json();
        if (!data?.success || !data?.data) return;
        
        if (isMounted) {
          setStakingApy(data.data);
        }
      } catch (err) {
        console.error("Failed to load staking APY data:", err);
      }
    };

    fetchApyData();
    const interval = setInterval(fetchApyData, 60000); // Poll every minute

    return () => {
      isMounted = false;
      clearInterval(interval);
    };
  }, []);

  const formatCurrency = (value, options = {}) => {
    const { maximumFractionDigits = 2, minimumFractionDigits = 2 } = options;
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits,
      minimumFractionDigits,
    }).format(Math.max(value, 0));
  };

  // eslint-disable-next-line no-unused-vars
  const formatCompactCurrency = (value) => {
    if (value >= 1_000_000) {
      return `$${(value / 1_000_000).toFixed(2)}M`;
    }
    if (value >= 1_000) {
      return `$${(value / 1_000).toFixed(1)}K`;
    }
    return formatCurrency(value);
  };

  const platformStats = [
    {
      label: "Total Users",
      value: String(apiStats.totalUsers),
      icon: Users,
    },
    {
      label: "Accounts Closed",
      value: String(apiStats.totalClosedAccounts),
      icon: Wallet,
    },
    {
      label: "SOL Refunded",
      value: String(apiStats.solRefunded.toFixed(2)),
      suffix: " SOL",
      icon: Coins,
    },
  ];

  const tokenStats = [
    {
      label: "Price",
      value: String(apiStats.price.toFixed(6)),
      prefix: "$",
      icon: DollarSign,
    },
    {
      label: "Market Cap",
      value: apiStats.marketCap >= 1_000_000
        ? (apiStats.marketCap / 1_000_000).toFixed(2)
        : apiStats.marketCap >= 1_000
        ? (apiStats.marketCap / 1_000).toFixed(1)
        : String(apiStats.marketCap.toFixed(2)),
      prefix: "$",
      suffix: apiStats.marketCap >= 1_000_000 ? "M" : apiStats.marketCap >= 1_000 ? "K" : "",
      icon: TrendingUp,
    },
    {
      label: "Holders",
      value: String(apiStats.holders),
      icon: UserCheck,
    },
    {
      label: "Total Staked",
      value: String(apiStats.totalStakedPercent.toFixed(2)),
      suffix: "%",
      icon: Lock,
    },
    {
      label: "Burned",
      value: String(apiStats.percentBurned.toFixed(2)),
      suffix: "%",
      icon: Flame,
    },
  ];

  return (
    <section
      id="stats"
      className="relative py-16 sm:py-24 bg-[#0f0f15] border-t border-white/5"
    >
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_0%,rgba(126,34,206,0.15),transparent_70%)]" />
      <div className="relative max-w-6xl mx-auto px-6 text-center">
        <h3 className="text-3xl font-bold mb-8 sm:mb-12 gradient-text">
          Live Stats
        </h3>

        {error && (
          <p className="text-sm text-red-400 mb-4">{error}</p>
        )}

        {/* Platform Stats */}
        <div className="mb-12">
          <SectionTitle>Platform Stats</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {isLoading ? (
              <>
                <StatSkeleton delay={0} />
                <StatSkeleton delay={0.1} />
                <StatSkeleton delay={0.2} />
              </>
            ) : (
              platformStats.map((stat, i) => (
                <CountUpCard key={stat.label} {...stat} delay={i * 0.05} />
              ))
            )}
          </div>
        </div>

        {/* Token Stats */}
        <div className="mb-12">
          <SectionTitle>$RYS Token Stats</SectionTitle>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-6">
            {isLoading ? (
              <>
                <StatSkeleton delay={0.15} />
                <StatSkeleton delay={0.2} />
                <StatSkeleton delay={0.25} />
                <StatSkeleton delay={0.3} />
                <StatSkeleton delay={0.35} />
              </>
            ) : (
              tokenStats.map((stat, i) => (
                <FadeInCard key={stat.label} {...stat} delay={0.15 + i * 0.08} />
              ))
            )}
          </div>
        </div>

        {/* Extended Stats Toggle */}
        <button
          onClick={() => setShowExtended(!showExtended)}
          className="mx-auto flex items-center gap-2 px-6 py-3 rounded-full glass hover:bg-white/10 transition-colors text-gray-300 text-sm font-medium"
        >
          {showExtended ? "Hide" : "Show"} Extended Stats
          {showExtended ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
        </button>

        {/* Extended Stats */}
        {showExtended && (
          <div
            className="mt-12 animate-fade-in-up"
            style={{ WebkitBackfaceVisibility: 'hidden' }}
          >
            {/* Claim Records */}
            <div className="mb-10">
              <SectionTitle>Claim Records</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="glass rounded-xl p-4 text-center">
                  <Trophy className="w-5 h-5 text-yellow-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Highest Claim (All Time)</p>
                  <p className="text-xl font-bold text-yellow-400">{extendedStats.highestBurnAllTime.toFixed(2)} SOL</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <ArrowUpRight className="w-5 h-5 text-green-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Highest Claim (24h)</p>
                  <p className="text-xl font-bold text-green-400">{extendedStats.highestBurn24h.toFixed(4)} SOL</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <ArrowUpRight className="w-5 h-5 text-blue-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Highest Claim (7d)</p>
                  <p className="text-xl font-bold text-blue-400">{extendedStats.highestBurn7d.toFixed(4)} SOL</p>
                </div>
              </div>
            </div>

            {/* Bot & Community */}
            <div className="mb-10">
              <SectionTitle>Bot & Community</SectionTitle>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="glass rounded-xl p-4 text-center">
                  <Twitter className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">X Accounts Connected</p>
                  <p className="text-xl font-bold text-white">{extendedStats.xAccountsConnected.toLocaleString()}</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <Bot className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Bot Users</p>
                  <p className="text-xl font-bold text-white">{extendedStats.botUsersTotal.toLocaleString()}</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <Wallet className="w-5 h-5 text-purple-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Wallets Connected</p>
                  <p className="text-xl font-bold text-white">{extendedStats.walletsConnected.toLocaleString()}</p>
                </div>
              </div>
            </div>

            {/* Finance */}
            <div className="mb-10">
              <SectionTitle>Finance</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 max-w-3xl mx-auto">
                <div className="glass rounded-xl p-4 text-center">
                  <Coins className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Staking Rewards Paid</p>
                  <p className="text-xl font-bold text-emerald-400">{extendedStats.stakingRewardsSol.toFixed(2)} SOL</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <RefreshCw className="w-5 h-5 text-cyan-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Total SOL Used for Buybacks</p>
                  <p className="text-xl font-bold text-cyan-400">{extendedStats.buybacksSol.toFixed(2)} SOL</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <Coins className="w-5 h-5 text-emerald-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">SOL Returned & Saved (Incorrect Burns)</p>
                  <p className="text-xl font-bold text-emerald-400">{extendedStats.solSavedForUsers.toFixed(2)} SOL</p>
                </div>
              </div>
            </div>

            {/* Payouts & Rewards */}
            <div className="mb-10">
              <SectionTitle>Payouts & Rewards</SectionTitle>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 max-w-2xl mx-auto">
                <div className="glass rounded-xl p-4 text-center">
                  <Zap className="w-5 h-5 text-orange-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Raid Payouts</p>
                  <p className="text-xl font-bold text-orange-400">${extendedStats.raidPayoutsSol.toFixed(0)} USD</p>
                </div>
                <div className="glass rounded-xl p-4 text-center">
                  <Trophy className="w-5 h-5 text-pink-400 mx-auto mb-2" />
                  <p className="text-xs text-gray-500 mb-1">Contest Payouts</p>
                  <p className="text-xl font-bold text-pink-400">
                    {extendedStats.contestPayoutsSol > 0 ? `${extendedStats.contestPayoutsSol.toFixed(2)} SOL` : 'Coming Soon'}
                  </p>
                </div>
              </div>
            </div>

            {/* Staking APY - 7 Day Breakdown */}
            <div>
              <SectionTitle>
                <Percent className="w-5 h-5 text-purple-400" />
                7-Day Staking APY
              </SectionTitle>
              
              {/* Average APY Card */}
              <div className="glass rounded-xl p-5 text-center mb-6 max-w-md mx-auto border border-purple-500/30">
                <p className="text-xs text-gray-400 mb-2">Average APY (Last 7 Days)</p>
                <p className="text-4xl font-bold text-purple-400">
                  {stakingApy.days.length === 0
                    ? 'Collecting...'
                    : stakingApy.validDays === 0
                    ? 'Pending'
                    : `${stakingApy.averageApy.toFixed(1)}%`}
                </p>
                <p className="text-xs text-gray-500 mt-2">
                  Based on 50% of platform revenue → staking pool
                </p>
                {stakingApy.days.length > 0 && stakingApy.validDays === 0 && (
                  <p className="text-[11px] text-gray-500 mt-2">
                    Waiting on SOL/USD snapshots (backfill will populate shortly).
                  </p>
                )}
              </div>

              {/* Daily Breakdown */}
              {stakingApy.days.length > 0 ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-7 gap-3 max-w-6xl mx-auto">
                  {stakingApy.days.map((day, idx) => (
                    <div 
                      key={day.date || idx} 
                      className="glass rounded-xl p-3 text-center hover:border-purple-500/40 transition-colors border border-transparent"
                    >
                      <div className="flex items-center justify-center gap-1 mb-2">
                        <Calendar className="w-3 h-3 text-gray-500" />
                        <p className="text-xs font-medium text-gray-300">{day.label}</p>
                      </div>
                      
                      <div className="space-y-1.5">
                        <div>
                          <p className="text-[10px] text-gray-500">Revenue</p>
                          <p className="text-sm font-semibold text-white">
                            {day.revenue.toFixed(2)} SOL
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-[10px] text-gray-500">Staking Pool (50%)</p>
                          <p className="text-sm font-semibold text-emerald-400">
                            {day.stakingPool.toFixed(2)} SOL
                          </p>
                        </div>
                        
                        <div>
                          <p className="text-[10px] text-gray-500">Token Price</p>
                          <p className="text-sm font-semibold text-gray-300">
                            {day.priceInSol > 0 
                              ? `${day.priceInSol.toFixed(8)} SOL` 
                              : '—'}
                          </p>
                        </div>
                        
                        <div className="pt-1 border-t border-white/10">
                          <p className="text-[10px] text-gray-500">Daily APY</p>
                          <p className={`text-lg font-bold ${
                            day.apy >= 20 ? 'text-lime-400' :
                            day.apy >= 10 ? 'text-emerald-400' :
                            day.apy > 0 ? 'text-yellow-400' : 'text-gray-500'
                          }`}>
                            {day.apy > 0 ? `${day.apy.toFixed(1)}%` : '—'}
                          </p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center text-gray-500 py-8">
                  <p>Collecting data... APY breakdown will appear once we have enough daily snapshots.</p>
                </div>
              )}

              {/* Total staking pool note */}
              {stakingApy.totalStakingPool > 0 && (
                <p className="text-xs text-gray-500 text-center mt-4">
                  Total 7-day staking pool: <span className="text-emerald-400 font-medium">{stakingApy.totalStakingPool.toFixed(2)} SOL</span>
                </p>
              )}
            </div>
          </div>
        )}
      </div>
    </section>
  );
}
